import NewHeader from 'components/NewHeader';
import FloatingController from 'components/Common/FloatingController';
import { PageOuterContainer } from 'components/Common/CommonWrappers';
import { BypassBlock } from 'components/Global/index';
import DeepfieldMainVisual from 'components/Product/DeepfieldMainVisual';
import { useDisablePinchZoom } from 'hooks/useDisablePinchZoomIn';
import { useSetScreenSize } from 'hooks/useSetScreenSize';
import Head from 'next/head';
import styled from 'styled-components';
import ProductSectionTabTemplate, {
  TabItem,
} from 'components/Product/ProductSectionTabTemplate';
import { useRouter } from 'next/router';
import {
  deepfieldContents,
  pulsarContents,
} from 'locale/productTemplateContents';
import {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import { handleScrollToHash } from 'utils';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { useForbidScroll } from 'hooks/useForbidScroll';
import { useRemoveScrollTop } from 'hooks/useRemoveScrollTop';

const deepfieldPathNames = ['sr', 'pqo'];
// const pulsarPathNames = ['av1', 'avif', 'vmaf'];
export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const pathNames = ['sr', 'pqo', 'av1', 'avif', 'vmaf'];
  const paths = [];

  // locale : ko, en 생성
  pathNames.forEach(pathname => {
    for (const locale of locales) {
      paths.push({ params: { p_name: pathname }, locale });
    }
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
}: GetStaticPropsContext) => {
  return {
    props: {
      pName: params.p_name,
    },
  };
};

const productDescription = [
  '인공지능 기반의 초해상화 기술로써 동영상의 시.공간적 정보를 활용하여 고화질 및 고해상도의 영상으로 변환합니다.',
  'It is an AI-powered Super Resolution technology that upscales videos to high resolution and improves video quality by using temporal and spatial information.',
];

const productItems: TabItem[] = [
  {
    label: 'DeepField-SR',
    pathname: '/product/sr',
  },
  {
    label: 'DeepField-PQO',
    pathname: '/product/pqo',
  },
  {
    label: 'Pulsar-AV1',
    pathname: '/product/av1',
  },
  {
    label: 'Pulsar-AVIF',
    pathname: '/product/avif',
  },
  {
    label: 'Pulsar-VMAF',
    pathname: '/product/vmaf',
  },
];

const DeepfieldPage = ({
  pName,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { asPath, locale } = useRouter();

  const [$fakeHeaderRef, isVisible, fakeHeaderEntry] = useInView({
    fallbackInView: true,
  });
  const [$mainVisualRef, isVisibleMainVisual, mainVisualEntry] = useInView({
    fallbackInView: true,
  });
  const productName = deepfieldPathNames.includes(pName)
    ? 'DeepField'
    : 'Pulsar';
  const contents = deepfieldPathNames.includes(pName)
    ? deepfieldContents
    : pulsarContents;
  const productTabItems = productItems.filter(item =>
    item.label.includes(productName)
  );

  useDisablePinchZoom();
  useSetScreenSize();
  useForbidScroll();
  useRemoveScrollTop();
  useEffect(() => {
    handleScrollToHash();
  }, [asPath]);

  return (
    <DeepfieldPageContainer>
      <Head>
        <title>{contents[pName].banner.bannerTitle} - BLUEDOT</title>
        <meta
          name="description"
          content={contents[pName].banner.bannerText[locale]}
          key="description"
        />
        <meta
          property="og:description"
          content={contents[pName].banner.bannerText[locale]}
          key="og:description"
        />
        <meta
          property="twitter:description"
          content={contents[pName].banner.bannerText[locale]}
          key="twitter:description"
        />
      </Head>

      <BypassBlock contentSectionId="#product-content1" />
      <div className="io-target" ref={$fakeHeaderRef} />
      <NewHeader isScrolled={!isVisible && !!fakeHeaderEntry?.target} />
      <DeepfieldMainVisual ref={$mainVisualRef} />

      <ProductSectionTabTemplate
        productName={productName}
        productDescription={productDescription}
        tabItems={productTabItems}
        isMainVisualHidden={
          typeof window === 'undefined'
            ? false
            : !isVisibleMainVisual && !!mainVisualEntry?.target
        }
        pName={pName}
      />

      <FloatingController isVisible={isVisible} />
    </DeepfieldPageContainer>
  );
};

export default DeepfieldPage;

const DeepfieldPageContainer = styled(PageOuterContainer)``;
